import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { useRouteMatch } from 'react-router-dom'
import { ClientRiskScoreEnum } from '@sparling/financial-common'

import { GreenButton } from '../../../components/Buttons/GreenButton'
import { icons } from '../../../constants/icons'
import { useStyles } from './useStyles'
import { IData } from '../UserProfileTabs'
import { AppStateContext, useRefresh, useRest } from '../../../components/Context'
import { MemoUploadFile } from './UploadFile'
import { MemoChangeStatuses } from './ChangeStatuses'
import { routes } from '../../../constants/routes'

interface UserDetailsProps {
  data?: IData
}

export const UserKYC: FC<UserDetailsProps> = ({ data }) => {
  const classes = useStyles()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { getIsRefresh } = useContext(AppStateContext)
  const [statusSumSub, setStatusSumSub] = useState('')
  const [files, setFiles] = useState([])
  const [isOpenStatus, setIsOpenStatus] = useState(false)
  const [isOpenUpload, setIsOpenUpload] = useState(false)
  const [refresh, setRefresh] = useState<boolean | undefined>()

  const isUserRoute = !!useRouteMatch(routes.USER_DETAILS_TABS)
  const idSf = isUserRoute ? data?.idSf : data?.adminIdSf

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const loadKycStatus = useCallback(async () => {
    rxios.get(`sumsub/user-status/${idSf}`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setStatusSumSub(next.status)
        saveIsRefresh(false)
      },
      (error) => {
        toast.error(error)
      },
    )
  }, [rxios, saveIsRefresh, idSf])

  const loadData = useCallback(async () => {
    rxios.post(`gcs/storage-documents`, { userIdSf: idSf }).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setFiles(next)
        saveIsRefresh(false)
      },
      (error) => {
        toast.error(error)
      },
    )
  }, [rxios, saveIsRefresh, idSf])

  useEffect(() => {
    ;(async () => {
      await loadKycStatus()
      await loadData()
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (refresh) {
      ;(async () => {
        await loadKycStatus()
        await loadData()
      })()
    }
    // eslint-disable-next-line
  }, [refresh])

  const toggleStatusModal = useCallback(() => {
    setIsOpenStatus((prevState) => !prevState)
  }, [setIsOpenStatus])

  const toggleUploadModal = useCallback(() => {
    setIsOpenUpload((prevState) => !prevState)
  }, [setIsOpenUpload])

  const downloadFile = useCallback(
    async (id: string, name: string) => {
      rxios
        .get<Blob>(`/gcs/download/${id}/${name}`, {
          responseType: 'blob',
        })
        .subscribe(
          // eslint-disable-next-line
          (response: any) => {
            const { file, fileType, fileName } = response

            const resp = Uint8Array.from(file[0].data)
            const content = new Blob([resp.buffer], { type: fileType })

            const encodedUri = window.URL.createObjectURL(content)
            const link = document.createElement('a')

            link.setAttribute('href', encodedUri)
            link.setAttribute('download', fileName)

            link.click()
          },
          (error) => {
            toast.error(error)
          },
        )
    },
    [rxios],
  )

  return (
    <>
      {idSf && (
        <>
          <Grid container className={classes.container}>
            <Grid container item xs={12} className={classes.headerPosition}>
              <Grid container item xs={10}>
                <Typography variant="h4">KYC</Typography>
              </Grid>
              <Grid container item xs={2} className={classes.buttonWrap}>
                <GreenButton text="Edit" width={120} onClick={toggleStatusModal} />
              </Grid>
            </Grid>

            <Grid container>
              <Grid container item xs={5} className={classes.columnWrap}>
                <Grid item xs={12} className={classes.fieldStatus}>
                  <Grid item xs={5}>
                    <Typography component="span">SumSub User Status: </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography component="span">{statusSumSub || '-'}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item xs={5} className={classes.columnWrap}>
                <Grid item xs={12} className={classes.fieldStatus}>
                  <Grid item xs={5}>
                    <Typography component="span">Internal User Status: </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography component="span">{data?.internalKycStatus || '-'}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container>
              <Grid container item xs={5} className={classes.columnWrap}>
                <Grid item xs={12} className={classes.fieldStatus}>
                  <Grid item xs={5}>
                    <Typography component="span">User Risk Score: </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography component="span">{data?.riskScore || '-'}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid container item xs={12} className={classes.headerPosition}>
              <Grid container item xs={10}>
                <Typography variant="h4">KYC Documents</Typography>
              </Grid>
              <Grid container item xs={2} className={classes.buttonWrap}>
                <GreenButton prefix={icons.CLOUD} text="Upload" width={120} onClick={toggleUploadModal} />
              </Grid>
            </Grid>

            <Grid container item xs={12} className={classes.tableColor}>
              <Grid item xs={6} sm={3} className={classes.field}>
                <Typography className={classes.field}>Type </Typography>
              </Grid>
              <Grid item xs={6} sm={6} className={classes.field}>
                <Typography className={classes.field}>Document</Typography>
              </Grid>
              <Grid item xs={6} sm={3} className={classes.field}>
                <Typography className={classes.field}>Action</Typography>
              </Grid>

              {files?.map(
                // eslint-disable-next-line
                (item: any, index: any) => (
                  <Grid container key={index} className={classes.row}>
                    <Grid item xs={6} sm={3} className={classes.column}>
                      <Typography className={classes.subTitle}>{item.documentType}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={6} className={classes.column}>
                      <Typography className={classes.subTitle}>{item.fileName}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3} className={classes.field}>
                      <GreenButton
                        prefix={icons.DOWNLOAD}
                        text="Download"
                        width={120}
                        height={32}
                        onClick={() => downloadFile(idSf ?? '', item.fileName)}
                      />
                    </Grid>
                  </Grid>
                ),
              )}
            </Grid>
          </Grid>

          <MemoChangeStatuses
            isOpen={isOpenStatus}
            modalClose={toggleStatusModal}
            idSf={idSf}
            status={data?.internalKycStatus}
            riskScore={data?.riskScore as ClientRiskScoreEnum | undefined}
          />
          <MemoUploadFile isOpen={isOpenUpload} modalClose={toggleUploadModal} idSf={idSf} />
        </>
      )}
    </>
  )
}
